import { useId, useState } from "react"
import {
	IconClockOutline18 as Clock,
	IconDotsOutline18 as Dots,
	IconEyeOutline18 as Eye,
	IconEyeSlashOutline18 as EyeOff,
	IconMoveToRightOutline18 as MoveToPortal,
	IconSquareArrowUpRightOutline18 as NewTab,
	IconPlusOutline18 as Plus,
	IconLayersOutline18 as Stack,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { Avatar, Badge, Button, IconButton, Tooltip } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"

import type { RoadmapTab } from "./nav-items"
import { useOpenNewPortal } from "@/lib/hooks/use-open-new-portal"
import { useNewPortalHintState } from "@/lib/states/new-portal-hint-state"
import { useRole } from "@/lib/useRole"
import { useZeroWorkspace } from "@/lib/zero"
import { NavRowActions } from "../helpCenter/nav-row-actions"
import { useActiveRoadmapView } from "./filter/roadmap-views"
import { MoveRequestsToPortalDialog } from "./move-requests-to-portal-dialog"

export interface PortalTreeOption {
	id: string | null
	name: string
	logoUrl: string | null
	colorSeed?: string | null
	/** The portal's feature requests page, opened in a new tab from the row. */
	url?: string | null
}

const VIEW_ITEMS: Array<{
	value: RoadmapTab
	label: string
	Icon: typeof Eye
}> = [
	{ value: "pending", label: "Pending", Icon: Clock },
	{ value: "active", label: "Visible", Icon: Eye },
	{ value: "hidden", label: "Hidden", Icon: EyeOff },
	{ value: "all", label: "All requests", Icon: Stack },
]

const ROW =
	"flex h-[1.9rem] w-full min-w-0 items-center gap-2 rounded-md pl-2 pr-2 text-sm font-medium"
const SELECTED = "bg-gray-100 text-primary dark:bg-gray-800"
const IDLE = "bg-transparent hover:bg-gray-100 hover:dark:bg-gray-800/50"

const portalKey = (id: string | null) => id ?? "__main__"

/**
 * The views span every portal. Below them, each portal is one page: what that
 * portal shows its customers.
 */
export function PortalTree({
	portals,
	activePortalId,
	onPortalChange,
	tabValue,
	onTabChange,
	pendingCount,
	isPendingCountCapped,
	onSelect,
}: {
	portals: PortalTreeOption[]
	/** The open portal page; `undefined` while a view is open. */
	activePortalId: string | null | undefined
	onPortalChange: (id: string | null) => void
	tabValue: RoadmapTab
	onTabChange: (value: RoadmapTab) => void
	pendingCount: number
	isPendingCountCapped?: boolean
	onSelect?: () => void
}) {
	const { role } = useRole()
	const canEdit = role !== undefined && role !== "VIEWER"
	const [movingPortal, setMovingPortal] = useState<PortalTreeOption | null>(
		null,
	)
	// A workspace on the main portal alone has no portal pages to list.
	const isSinglePortal = portals.length <= 1
	const isPortalOpen = activePortalId !== undefined
	const pendingLabel = isPendingCountCapped ? `${pendingCount}+` : pendingCount

	const activeViewId = useActiveRoadmapView((s) => s.activeViewId)

	const openNewPortal = useOpenNewPortal()
	const workspaceId = useZeroWorkspace()?.id
	const isHintDismissed = useNewPortalHintState((s) =>
		workspaceId ? s.dismissedWorkspaceIds.includes(workspaceId) : false,
	)
	const dismissHint = useNewPortalHintState((s) => s.dismiss)
	// The dismissal is per workspace, so a lone portal shows neither until it loads.
	const showHint =
		canEdit && isSinglePortal && !!workspaceId && !isHintDismissed
	const showNewPortalRow = canEdit && (!isSinglePortal || isHintDismissed)
	const handleNewPortal = () => {
		openNewPortal()
		onSelect?.()
	}

	return (
		<div className="px-2.5 py-3">
			<ul className="flex list-none flex-col gap-px">
				{VIEW_ITEMS.map(({ value, label, Icon }) => {
					const isSelected =
						!isPortalOpen && !activeViewId && tabValue === value
					const showCount = value === "pending" && pendingCount > 0
					return (
						<li key={value}>
							<button
								type="button"
								onClick={() => {
									onTabChange(value)
									onSelect?.()
								}}
								data-view={value}
								aria-current={isSelected ? "page" : undefined}
								className={cn(ROW, isSelected ? SELECTED : IDLE)}
							>
								<Icon
									className="size-3.5 flex-none text-secondary"
									strokeWidth={1}
								/>
								<span className="flex-1 truncate text-start">{label}</span>
								{showCount && (
									<Badge
										variant="transparent"
										size="xs"
										className={cn("min-w-5 text-[10px] text-tertiary", {
											"text-primary": isSelected,
										})}
									>
										{pendingLabel}
									</Badge>
								)}
							</button>
						</li>
					)
				})}
			</ul>

			{!isSinglePortal && (
				<ul className="mt-4 flex list-none flex-col gap-px">
					<li className="px-2 pb-1 text-xs font-medium text-tertiary">
						Portals
					</li>
					{portals.map((portal) => {
						const isSelected = isPortalOpen && portal.id === activePortalId
						return (
							<li key={portalKey(portal.id)} className="group/item relative">
								<button
									type="button"
									onClick={() => {
										onPortalChange(portal.id)
										onSelect?.()
									}}
									data-portal={portalKey(portal.id)}
									aria-current={isSelected ? "page" : undefined}
									className={cn(ROW, isSelected ? SELECTED : IDLE)}
								>
									<Avatar
										name={portal.name}
										image={portal.logoUrl}
										colorSeed={portal.colorSeed}
										roundness="sm"
										size="xxs"
										className="flex-none"
									/>
									<span className="min-w-0 flex-1 truncate text-start">
										{portal.name}
									</span>
								</button>
								{(canEdit || portal.url) && (
									<NavRowActions className="right-0" isSelected={isSelected}>
										{portal.url && (
											<Tooltip label="Open in a new tab">
												<a
													href={portal.url}
													target="_blank"
													rel="noreferrer"
													aria-label={`Open ${portal.name} in a new tab`}
													className="flex size-6 items-center justify-center rounded-md text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
												>
													<NewTab className="h-3 w-3" strokeWidth={1.75} />
												</a>
											</Tooltip>
										)}
										{canEdit && (
											<DropdownMenu
												className="w-60"
												contentProps={{ align: "end" }}
												trigger={
													<IconButton
														Icon={Dots}
														variant="ghost"
														size="md"
														iconStrokeWidth={1.75}
														iconClassName="h-3 w-3"
														className="relative text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
														type="button"
														aria-label={`${portal.name} actions`}
													/>
												}
											>
												<DropdownMenuItem
													Icon={MoveToPortal}
													onClick={() => setMovingPortal(portal)}
												>
													Move to portal
												</DropdownMenuItem>
											</DropdownMenu>
										)}
									</NavRowActions>
								)}
							</li>
						)
					})}
				</ul>
			)}

			{showNewPortalRow && (
				<ul
					className={cn(
						"flex list-none flex-col",
						isSinglePortal ? "mt-4" : "mt-px",
					)}
				>
					<li>
						<button
							type="button"
							onClick={handleNewPortal}
							className={cn(ROW, IDLE, "text-secondary")}
						>
							<Plus
								className="size-3.5 flex-none text-tertiary"
								strokeWidth={1}
							/>
							<span className="flex-1 truncate text-start">New portal</span>
						</button>
					</li>
				</ul>
			)}
			{showHint && workspaceId && (
				<NewPortalHint
					onCreate={handleNewPortal}
					onDismiss={() => dismissHint(workspaceId)}
				/>
			)}
			{movingPortal && (
				<MoveRequestsToPortalDialog
					isOpen
					source={{ id: movingPortal.id, name: movingPortal.name }}
					onClose={() => setMovingPortal(null)}
				/>
			)}
		</div>
	)
}

function NewPortalHint({
	onCreate,
	onDismiss,
}: {
	onCreate: () => void
	onDismiss: () => void
}) {
	const titleId = useId()
	return (
		<section
			aria-labelledby={titleId}
			className="mt-4 flex flex-col gap-2.5 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700/80 dark:bg-gray-800/40"
		>
			<div className="flex flex-col gap-1">
				<div className="flex items-start gap-2">
					<h3 id={titleId} className="flex-1 text-sm font-medium text-primary">
						One board per product
					</h3>
					<IconButton
						Icon={XIcon}
						variant="ghost"
						size="md"
						iconStrokeWidth={1.75}
						iconClassName="h-3 w-3"
						className="-mr-1.5 -mt-0.5 flex-none text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
						type="button"
						aria-label="Dismiss"
						onClick={onDismiss}
					/>
				</div>
				<p className="text-xs text-secondary">
					A portal is the public site where customers vote on requests. Each
					product can have its own.
				</p>
			</div>
			<Button
				variant="outline"
				size="sm"
				Icon={Plus}
				className="self-start"
				onClick={onCreate}
			>
				Create portal
			</Button>
		</section>
	)
}
